import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import SiteMarker from "./SiteMarker";
import MapPlantedAcres from "../plantedAcres/MapPlantedAcres";
import PlantedAcresForm from "../plantedAcres/PlantedAcresForm";
import Timeline from "../plantedAcres/Timeline";
import Notes from "components/common/notes/Notes";
import ConfirmationPopup from "components/common/ConfirmationPopup";
import ShareSites from "./ShareSites";
import EditSite from "./EditSite";
import { useSelector } from "react-redux";
import { setSelectedSite, setSites } from "reduxStore/slices/siteSlice";
import {
  setEditedPlantedAcre,
  setPlantedAcres,
} from "reduxStore/slices/plantedAcresSlice";
import { useDispatch } from "react-redux";
import {
  ARE_YOU_SURE_DELETE_PLANTED_ACRES,
  ARE_YOU_SURE_DELETE_SITE,
  PLANTED_ACRES_DELETE_ERROR,
  SITE_DELETE_ERROR,
  FULL_SCREEN,
  STREET_VIEW,
  LABEL_VIEW,
  PLANTED_ACRES_FETCH_ERROR,
  ARE_YOU_SURE_STOP_SHARE_SITE,
  SITE_STOP_SHARE_ERROR,
  ERROR_UPDATING_SHARE_DETAILS,
  SITE_SHARING_HAS_BEEN_STOPPED,
  SITE_HAS_BEEN_DELETED_SUCCESSFULLY,
  PLANTED_ACRES_HAS_BEEN_DELETED_SUCCESSFULLY,
} from "configurations/MessageConstants";
import {
  MAP_TILES,
  MODE_TYPES,
  NOTES_TYPES,
  SHARE_TYPES,
} from "configurations/Constants";
import PlantedAcresService from "services/PlantedAcresService";
import SiteService from "services/SiteService";
import { toast } from "react-toastify";

const InvalidateMapSize = ({ isFullScreen }) => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
  }, [isFullScreen, map]);
  return null;
};

const CenterToFirstSite = ({ sites }) => {
  const map = useMap();
  useEffect(() => {
    if (sites && sites.length > 0) {
      const { latitude, longitude } = sites[0].geometry;
      map.setView([latitude, longitude], 12);
    }
  }, [sites, map]);
  return null;
};

const CenterToSelectedSite = ({ selectedSite }) => {
  const map = useMap();
  useEffect(() => {
    if (selectedSite && selectedSite.geometry) {
      const { latitude, longitude } = selectedSite.geometry;
      map.setView([latitude, longitude], 12);
    }
  }, [selectedSite, map]);

  return null;
};

const SitesMap = () => {
  const dispatch = useDispatch();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [streetView, setStreetView] = useState(false);
  const [showLabel, setShowLabel] = useState(true);
  const sites = useSelector((state) => state.site.sites);
  const selectedSite = useSelector((state) => state.site.selectedSite);
  const savePlantedAcreSuccess = useSelector(
    (state) => state.plantedAcres.savePlantedAcreSuccess
  );
  const saveSiteSuccess = useSelector((state) => state.site.saveSiteSuccess);
  const shareSiteSuccess = useSelector((state) => state.site.shareSiteSuccess);

  const [showAddPlantedAcres, setShowAddPlantedAcres] = useState(false);
  const [showEditPlantedAcres, setShowEditPlantedAcres] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [
    showDeletePlantedAcresConfirmation,
    setShowDeletePlantedAcresConfirmation,
  ] = useState(false);
  const [showShareSite, setShowShareSite] = useState(false);
  const [showEditSite, setShowEditSite] = useState(false);
  const [showDeleteSiteConfirmation, setShowDeleteSiteConfirmation] =
    useState(false);
  const [showStopShareSiteConfirmation, setShowStopShareSiteConfirmation] =
    useState(false);
  const [showSiteNotes, setShowSiteNotes] = useState(false);
  const [showPlantedAcresNotes, setShowPlantedAcresNotes] = useState(false);

  const [selectedPlantedAcresForAction, setSelectedPlantedAcresForAction] =
    useState(null);
  const [selectedSiteForAction, setSelectedSiteForAction] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getPlantedAcresBySiteId();
  }, [selectedSite, dispatch]);

  const getPlantedAcresBySiteId = async () => {
    try {
      setIsLoading(true);
      if (selectedSite && selectedSite.id) {
        const response = await PlantedAcresService.getPlantedAcresBySiteId(
          selectedSite.id
        );
        dispatch(setPlantedAcres(response.items));
      }
    } catch (error) {
      console.error(PLANTED_ACRES_FETCH_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkerClick = async (site) => {
    dispatch(setSelectedSite(site));
    await getPlantedAcresBySiteId();
  };

  const handleAddPlantedAcres = () => {
    dispatch(setEditedPlantedAcre(null));
    setShowAddPlantedAcres(true);
  };

  const handleEditPlantedAcres = (plantedAcres) => {
    dispatch(setEditedPlantedAcre(plantedAcres));
    setShowEditPlantedAcres(true);
  };

  const handleShowTimeline = (plantedAcres) => {
    setSelectedPlantedAcresForAction(plantedAcres);
    setShowTimeline(true);
  };

  const handleDeletePlantedAcres = (plantedAcres) => {
    setSelectedPlantedAcresForAction(plantedAcres);
    setShowDeletePlantedAcresConfirmation(true);
  };

  const handleConfirmDeletePlantedAcres = async () => {
    if (!selectedPlantedAcresForAction) return;
    setIsDeleting(true);
    try {
      await PlantedAcresService.deletePlantedAcres(
        selectedSite.id,
        selectedPlantedAcresForAction.id
      );
      setShowDeletePlantedAcresConfirmation(false);
      setSelectedPlantedAcresForAction(null);
      getPlantedAcresBySiteId();
      toast.success(PLANTED_ACRES_HAS_BEEN_DELETED_SUCCESSFULLY);
    } catch (error) {
      console.error(PLANTED_ACRES_DELETE_ERROR, error);
      toast.error(error.response.data.detail);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleShareSite = (site) => {
    setSelectedSiteForAction(site);
    setShowShareSite(true);
  };

  const handleEditSite = (site) => {
    setSelectedSiteForAction(site);
    setShowEditSite(true);
  };

  const handleDeleteSite = (site) => {
    setSelectedSiteForAction(site);
    setShowDeleteSiteConfirmation(true);
  };

  const handleStopShareSite = (site) => {
    setSelectedSiteForAction(site);
    setShowStopShareSiteConfirmation(true);
  };

  const handleShowSiteNotes = (site) => {
    setSelectedSiteForAction(site);
    setShowSiteNotes(true);
  };

  const handleShowPlantedAcresNotes = (plantedAcres) => {
    setSelectedPlantedAcresForAction(plantedAcres);
    setShowPlantedAcresNotes(true);
  };

  const handleConfirmDeleteSite = async () => {
    if (!selectedSiteForAction) return;
    setIsDeleting(true);
    try {
      await SiteService.deleteSite(selectedSiteForAction.id);
      setShowDeleteSiteConfirmation(false);
      setSelectedSiteForAction(null);
      dispatch(
        setSites(
          sites.filter(
            (deletedSite) => deletedSite.id !== selectedSiteForAction.id
          )
        )
      );
      dispatch(setSelectedSite(null));
      toast.success(SITE_HAS_BEEN_DELETED_SUCCESSFULLY);
    } catch (error) {
      console.error(SITE_DELETE_ERROR, error);
      toast.error(error.response.data.detail);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmStopShare = async () => {
    if (!selectedSiteForAction) return;
    setIsDeleting(true);
    try {
      await SiteService.updateShare(
        selectedSiteForAction.sharingId,
        selectedSiteForAction.id,
        {
          active: false,
        }
      );
      setShowStopShareSiteConfirmation(false);
      setSelectedSiteForAction(null);
      dispatch(
        setSites(
          sites.filter(
            (stoppedSite) => stoppedSite.id !== selectedSiteForAction.id
          )
        )
      );
      dispatch(setSelectedSite(null));
      toast.success(SITE_SHARING_HAS_BEEN_STOPPED);
    } catch (error) {
      console.error(SITE_STOP_SHARE_ERROR, error);
      toast.error(ERROR_UPDATING_SHARE_DETAILS);
    } finally {
      setIsDeleting(false);
    }
  };

  const isRootModalOpen =
    showAddPlantedAcres ||
    showEditPlantedAcres ||
    showTimeline ||
    showDeletePlantedAcresConfirmation ||
    showShareSite ||
    showEditSite ||
    showDeleteSiteConfirmation ||
    showStopShareSiteConfirmation ||
    savePlantedAcreSuccess ||
    saveSiteSuccess ||
    shareSiteSuccess ||
    showSiteNotes ||
    showPlantedAcresNotes;

  useEffect(() => {
    if (!selectedSite) {
      setShowAddPlantedAcres(false);
      setShowEditPlantedAcres(false);
      setShowTimeline(false);
      setShowDeletePlantedAcresConfirmation(false);
      setShowShareSite(false);
      setShowEditSite(false);
      setShowDeleteSiteConfirmation(false);
      setShowStopShareSiteConfirmation(false);
      setSelectedPlantedAcresForAction(null);
      setSelectedSiteForAction(null);
      setShowSiteNotes(false);
      setShowPlantedAcresNotes(false);
    }
  }, [selectedSite]);

  return (
    <div
      className={`${
        isFullScreen ? "site-map-container-full" : "site-map-container"
      }`}
    >
      <MapContainer
        center={[33.5778, -101.8553]}
        zoom={12}
        minZoom={2}
        style={{ height: "100%", width: "100%" }}
      >
        {streetView ? (
          <TileLayer
            url={MAP_TILES.STREET_VIEW.url}
            attribution={MAP_TILES.STREET_VIEW.attribution}
          />
        ) : (
          <TileLayer
            url={
              showLabel
                ? MAP_TILES.SATELLITE_WITH_LABELS.url
                : MAP_TILES.SATELLITE_WITHOUT_LABELS.url
            }
            attribution={
              showLabel
                ? MAP_TILES.SATELLITE_WITH_LABELS.attribution
                : MAP_TILES.SATELLITE_WITHOUT_LABELS.attribution
            }
          />
        )}
        {sites?.map((site) => (
          <SiteMarker
            key={site.id}
            position={site.geometry}
            site={site}
            onMarkerClick={handleMarkerClick}
            type={`${site.isShared && "shared"}`}
          />
        ))}

        <InvalidateMapSize isFullScreen={isFullScreen} />
        <CenterToFirstSite sites={sites} />
        <CenterToSelectedSite selectedSite={selectedSite} />
      </MapContainer>
      {selectedSite && !isRootModalOpen && (
        <MapPlantedAcres
          onAddPlantedAcres={handleAddPlantedAcres}
          onEditPlantedAcre={handleEditPlantedAcres}
          onShowTimeline={handleShowTimeline}
          onDeletePlantedAcre={handleDeletePlantedAcres}
          onShareSite={handleShareSite}
          onEditSite={handleEditSite}
          onDeleteSite={handleDeleteSite}
          onStopShareSite={handleStopShareSite}
          onShowSiteNotes={handleShowSiteNotes}
          onShowPlantedAcresNotes={handleShowPlantedAcresNotes}
          isLoading={isLoading}
        />
      )}
      x
      <div className="site-map-controls d-flex flex-column">
        <Button
          variant={"light"}
          onClick={() => setIsFullScreen(!isFullScreen)}
          className={`site-map-expand-btn ${isFullScreen ? "active-view" : ""}`}
          title={FULL_SCREEN}
        >
          {isFullScreen ? (
            <i className="fa-solid fa-compress" />
          ) : (
            <i className="fa-solid fa-expand" />
          )}
        </Button>
        <Button
          variant={"light"}
          onClick={() => setStreetView(!streetView)}
          className={`site-map-expand-btn ${
            streetView ? "street-active-view" : ""
          }`}
          title={STREET_VIEW}
        >
          <i className="fa-solid fa-street-view" />
        </Button>
        {!streetView && (
          <Button
            variant={"light"}
            onClick={() => setShowLabel(!showLabel)}
            className={`site-map-expand-btn ${
              showLabel ? "label-active-view" : ""
            }`}
            title={LABEL_VIEW}
          >
            <i className="fa-solid fa-tags" />
          </Button>
        )}
      </div>
      <PlantedAcresForm
        mode={MODE_TYPES.ADD}
        show={showAddPlantedAcres}
        onClose={() => setShowAddPlantedAcres(false)}
      />
      <PlantedAcresForm
        mode={MODE_TYPES.EDIT}
        show={showEditPlantedAcres}
        onClose={() => setShowEditPlantedAcres(false)}
      />
      {showTimeline && selectedPlantedAcresForAction && (
        <Timeline
          show={showTimeline}
          handleClose={() => {
            setShowTimeline(false);
          }}
          plantedAcresId={selectedPlantedAcresForAction.id}
        />
      )}
      <ConfirmationPopup
        show={showDeletePlantedAcresConfirmation}
        title="Delete Planted Acres"
        message={
          selectedPlantedAcresForAction && (
            <span className="text-muted">
              {ARE_YOU_SURE_DELETE_PLANTED_ACRES}{" "}
              <strong>{selectedPlantedAcresForAction.name}</strong>?
            </span>
          )
        }
        onConfirm={handleConfirmDeletePlantedAcres}
        onCancel={() => {
          setShowDeletePlantedAcresConfirmation(false);
        }}
        confirmText="Delete"
        cancelText="Cancel"
        isDeleting={isDeleting}
      />
      {showShareSite && selectedSiteForAction && (
        <ShareSites
          type={SHARE_TYPES.SINGLE}
          selected={[selectedSiteForAction]}
          show={showShareSite}
          closeShare={() => {
            setShowShareSite(false);
          }}
        />
      )}
      {showEditSite && selectedSiteForAction && (
        <EditSite
          site={selectedSiteForAction}
          show={showEditSite}
          setShowEditSite={(value) => {
            setShowEditSite(value);
          }}
        />
      )}
      <ConfirmationPopup
        show={showDeleteSiteConfirmation}
        title="Delete Site"
        message={
          selectedSiteForAction && (
            <span className="text-muted">
              {ARE_YOU_SURE_DELETE_SITE}{" "}
              <strong>{selectedSiteForAction.name}</strong>?
            </span>
          )
        }
        onConfirm={handleConfirmDeleteSite}
        onCancel={() => {
          setShowDeleteSiteConfirmation(false);
        }}
        confirmText="Delete"
        cancelText="Cancel"
        isDeleting={isDeleting}
      />
      <ConfirmationPopup
        show={showStopShareSiteConfirmation}
        title="Stop Share Site"
        message={
          selectedSiteForAction && (
            <span className="text-muted">
              {ARE_YOU_SURE_STOP_SHARE_SITE}{" "}
              <strong>{selectedSiteForAction.name}</strong>?
            </span>
          )
        }
        onConfirm={handleConfirmStopShare}
        onCancel={() => {
          setShowStopShareSiteConfirmation(false);
        }}
        confirmText="Stop"
        cancelText="Cancel"
        isDeleting={isDeleting}
      />
      {showSiteNotes && selectedSiteForAction && (
        <Notes
          id={selectedSiteForAction.id}
          type={NOTES_TYPES.SITE}
          name={selectedSiteForAction.name}
          show={showSiteNotes}
          handleClose={() => setShowSiteNotes(false)}
        />
      )}
      {showPlantedAcresNotes && selectedPlantedAcresForAction && (
        <Notes
          id={selectedPlantedAcresForAction.id}
          type={NOTES_TYPES.PLANTED_ACRES}
          name={selectedPlantedAcresForAction.name}
          show={showPlantedAcresNotes}
          handleClose={() => setShowPlantedAcresNotes(false)}
        />
      )}
    </div>
  );
};

export default SitesMap;
